/**
 * The Run detail page: `/runs/:workItemId/:attemptNo`.
 *
 * The case file's live card, at full width, with the trail open and the
 * record's extras (a Receipt, a mission build's revised outputs) as more
 * folds, then the plan the run worked from. It used to be a five-tab record
 * built for mission builds, and every planning run read as a "legacy run" to
 * it: placeholders in every tab. The card is the surface people read a run
 * by, so the page is that card, and the record adds only what it holds.
 *
 * The attempt number stays the URL handle (run UUIDs never appear in it) and
 * nowhere a person reads: it counts every run on the item whatever its
 * purpose, so a run is named by `runLabel` instead. With no runs yet the page
 * falls back to the owning case file, exactly like the legacy redirect did.
 */
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import type { WorkItem, WorkItemRunListEntry } from "@yagni/shared";
import { runUrl, workItemUrl } from "@yagni/shared";
import { AUTHENTICATED_DEFAULT_PATH } from "../../routes/defaults.js";
import {
  caseRunStatusLabel,
  formatMillicents,
  RUN_TONE,
  useWorkItemRuns,
} from "../../work/useWorkItemRuns.js";
import {
  hasRecordedPlan,
  Plan,
  RunRecordFolds,
  useWorkMissionView,
} from "../../work/WorkMissionDetail.js";
import { LiveStream, hhmm, type LiveStreamMode } from "../../work/LiveStream.js";
import { StageTracker } from "../../work/StageTracker.js";
import { runLabel } from "../../work/runLabel.js";
import { useWorkerLookup } from "../../work/useWorkerLookup.js";
import { useShowsRunCost } from "../../hooks/useShowsRunCost.js";
import type { PipelineStage } from "../../work/workStage.js";
import "../../work/work.css";
import "../../work/caseFile.css";
import "./runDetail.css";

export { BUILT_WITHOUT_ENVIRONMENT } from "../../work/WorkMissionDetail.js";

/**
 * Live is a run that has not finished and is moving. Waiting is one that has
 * not finished and is parked on a person (a plan waiting for approval, a
 * build parked on its budget): still this run, but not working, so the card
 * must not say it is drafting. Stopped is the case file's own word (a failed
 * run, or a build that ended on a recorded stop). Everything else finished.
 */
export function runPageMode(run: WorkItemRunListEntry): LiveStreamMode {
  const tone = RUN_TONE[run.status];
  if (run.finishedAt == null && tone === "running") return "live";
  if (run.finishedAt == null && tone === "paused") return "waiting";
  const word = caseRunStatusLabel(run);
  if (word === "stopped" || word === "failed") return "stopped";
  return "done";
}

/** The stage the card's working clause speaks for, from what the run was for. */
export function runPageStage(run: Pick<WorkItemRunListEntry, "purpose" | "kind">): PipelineStage {
  if (run.purpose === "planning") return "plan";
  if (run.purpose === "scoping") return "proposed";
  if (run.kind === "review") return "review";
  if (run.kind === "qa") return "qa";
  return "build";
}

export function RunDetailPage() {
  const params = useParams<{ workItemId?: string; attemptNo?: string }>();
  const workItemId = params.workItemId ?? "";
  const attemptNo = Number(params.attemptNo);

  const { runs, totalSellMillicents, loading } = useWorkItemRuns(workItemId);
  const run = runs.find((r) => r.attemptNo === attemptNo) ?? null;
  const {
    view,
    loading: viewLoading,
    error,
    reload,
  } = useWorkMissionView(workItemId, run?.id ?? null, run != null);
  const workerOf = useWorkerLookup();
  const showsCost = useShowsRunCost();

  const [item, setItem] = useState<WorkItem | null>(null);
  useEffect(() => {
    if (!workItemId) return;
    let cancelled = false;
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}`, { credentials: "include" })
      .then((r) => (r.ok ? (r.json() as Promise<WorkItem>) : null))
      .then((data) => {
        if (!cancelled && data) setItem(data);
      })
      .catch(() => {
        // The header degrades to "Back to the case file"; the record still loads.
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId]);

  if (!workItemId || Number.isNaN(attemptNo)) {
    return <Navigate to={AUTHENTICATED_DEFAULT_PATH} replace />;
  }
  if (!loading && runs.length === 0) {
    // No runs yet: land on the owning record, like the legacy redirect did.
    return <Navigate to={workItemUrl(workItemId, item?.deskId)} replace />;
  }
  if (!loading && !run && runs[0]) {
    return <Navigate to={runUrl(workItemId, runs[0].attemptNo)} replace />;
  }

  // Null on the pull-request plan: the head drops the price and the total.
  const price = showsCost && run ? formatMillicents(run.sellMillicents) : null;
  const total = showsCost ? formatMillicents(totalSellMillicents) : null;
  const mode = run ? runPageMode(run) : null;

  return (
    <div className="rp-page" data-testid="run-detail-page">
      <header className="rp-head">
        <Link className="rp-back" to={workItemUrl(workItemId, item?.deskId)}>
          <ArrowLeft width={13} height={13} aria-hidden />
          {item ? `${item.identifier} · ${item.title}` : "Back to the case file"}
        </Link>
        <div className="rp-title-row">
          <h1>{run ? runLabel(run, runs) : "Run"}</h1>
          {run && (
            <span className="rp-status" data-tone={RUN_TONE[run.status]}>
              <span className="rp-dot" aria-hidden />
              {caseRunStatusLabel(run)}
            </span>
          )}
          {price && <span className="rp-price">{price}</span>}
        </div>
        {runs.length > 1 && (
          <nav className="rp-runs" aria-label="Runs on this ticket">
            {runs
              .slice()
              .sort((a, b) => a.attemptNo - b.attemptNo)
              .map((r) => {
                const active = r.attemptNo === attemptNo;
                const clock = hhmm(r.startedAt);
                return (
                  <Link
                    key={r.id}
                    to={runUrl(workItemId, r.attemptNo)}
                    className={active ? "is-active" : ""}
                    data-tone={RUN_TONE[r.status]}
                    aria-current={active ? "page" : undefined}
                  >
                    <span className="rp-dot" aria-hidden />
                    {clock ? `${runLabel(r, runs)} · ${clock}` : runLabel(r, runs)}
                  </Link>
                );
              })}
            {total != null && <span className="rp-total">{total} total</span>}
          </nav>
        )}
      </header>
      {/* The build's map, plan, implement, review, fix rail. It only means
          something on a build that is still going, and it watches this run.
          The one-attempt lane's run carries every stage, its plan included. */}
      {run && (run.purpose === "execution" || run.lane === "attempt") && mode === "live" && (
        <StageTracker workItemId={workItemId} runId={run.id} />
      )}
      {run && mode ? (
        <LiveStream
          record
          workItemId={workItemId}
          run={run}
          runs={runs}
          workerName={workerOf(run.personaSlug)?.displayName ?? null}
          mode={mode}
          stage={runPageStage(run)}
          prNumber={item?.prNumber ?? null}
        >
          <RunRecordFolds view={view} loading={viewLoading} error={error} onRetry={reload} />
        </LiveStream>
      ) : (
        <p className="cf-mission-empty" role="status">
          Loading the record…
        </p>
      )}
      {view && hasRecordedPlan(view) && (
        <section className="rp-plan" data-testid="run-detail-plan">
          <div className="rp-kicker">The plan</div>
          <Plan view={view} />
        </section>
      )}
    </div>
  );
}
